import React, { useEffect } from 'react'
import { Box, Button, Container, Typography } from '@mui/material'
import { useRouteError } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import errorImage from '../assets/sad_cat.png'

/**
 * Router-level error boundary (errorElement). Without it, React Router's data router
 * swallows uncaught render errors and shows its own bare "Unexpected Application Error!"
 * page; errors caught this way also never reach Sentry on their own.
 */
const RouteErrorBoundary: React.FC = () => {
    const { t } = useTranslation()
    const error = useRouteError()

    useEffect(() => {
        Sentry.captureException(error)
    }, [error])

    // Full page load instead of client-side navigation: after a render crash the app
    // state may be corrupted, and if the crash happened on the home route itself a
    // client-side <Link to="/"> would not remount anything.
    const goHome = () => {
        window.location.assign('/')
    }

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <img src={errorImage} alt="" style={{ width: '60px', opacity: 0.6 }} />
                <Typography component="h1" variant="h4" sx={{ mt: 2 }}>
                    {t('errors.error_page.title')}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('errors.error_page.unexpected_message')}
                </Typography>
                <Button onClick={goHome} sx={{ mt: 3, mb: 2 }}>
                    {t('errors.error_page.back_home')}
                </Button>
            </Box>
        </Container>
    )
}

export default RouteErrorBoundary
